/**
 * 石碑(方案 C6):圆顶碑 + ember 诗魂孔;落成庄重升起 + 底部 divine 尘环 + 轻震屏;
 * 悬停浮出毛玻璃诗卡。沿广场弧线排列。
 */
import { Container, Graphics, Sprite, FederatedPointerEvent } from 'pixi.js';
import { Stage, LW } from '../engine/Stage';
import { sky } from '../engine/SkyRenderer';
import { eventPool } from '../engine/ParticlePool';
import { TEX } from '../engine/Tex';
import { P } from '../engine/Palette';
import { easing } from '../engine/Easing';
import { rand } from '../engine/util';

export interface Stone { text: string; author: string; }

interface StType { stone: Stone; node: Container; g: Graphics; soul: Sprite; rise: number; baseY: number; x: number; }

const CX = LW * 0.5;

export class Steles {
  private list: StType[] = [];
  private layer: Container;
  private tooltip: HTMLElement;
  private t = 0;

  constructor(private stage: Stage, private surfaceYAt: (x: number) => number, private onRise?: (x: number) => void) {
    this.layer = stage.layers.mainIsland;
    this.tooltip = document.createElement('div');
    this.tooltip.className = 'stone-tip';
    document.body.appendChild(this.tooltip);
  }

  get count() { return this.list.length; }

  sync(stones: Stone[]) {
    while (this.list.length < stones.length) this.addStele(stones[this.list.length]);
  }

  private plazaX(i: number): number {
    // 右台地弧线,满12座换第二排错半格
    const row = Math.floor(i / 12), col = i % 12;
    return CX + 140 + col * 34 + (row % 2) * 17;
  }

  private addStele(stone: Stone) {
    const i = this.list.length;
    const x = this.plazaX(i);
    const baseY = this.surfaceYAt(x) + 2;
    const node = new Container();
    node.position.set(x, baseY);
    const g = new Graphics();
    const soul = new Sprite(TEX.glow());
    soul.anchor.set(0.5); soul.blendMode = 'add'; soul.tint = P.emberHi; soul.scale.set(0.05); soul.position.set(0, -34);
    node.addChild(g, soul);
    node.eventMode = 'static'; node.cursor = 'pointer';
    node.hitArea = { contains: (px: number, py: number) => px >= -12 && px <= 12 && py >= -64 && py <= 4 } as any;
    node.on('pointerover', (e: FederatedPointerEvent) => this.showTip(stone, e));
    node.on('pointermove', (e: FederatedPointerEvent) => this.moveTip(e));
    node.on('pointerout', () => this.tooltip.classList.remove('show'));
    this.layer.addChild(node);
    const st: StType = { stone, node, g, soul, rise: 0, baseY, x };
    this.list.push(st);
    // 尘环 + 震屏 + 烛民围拢
    for (let k = 0; k < 14; k++) {
      const a = (k / 14) * 6.28;
      eventPool?.spawn({ x: x + Math.cos(a) * 8, y: baseY, vx: Math.cos(a) * 40, vy: Math.sin(a) * 20 - 20, life: 1, scale: 0.03, tint: P.divine, alpha: 0.7, blend: 'add', texture: TEX.glow(), fade: 'quad' });
    }
    this.onRise?.(x);
  }

  update(dt: number) {
    this.t += dt;
    const sil = sky.silhouette;
    for (const st of this.list) {
      if (st.rise < 1) { st.rise = Math.min(1, st.rise + dt / 2); st.node.y = st.baseY + (1 - easing.quadOut(st.rise)) * 64; }
      st.g.clear();
      // 圆顶碑
      st.g.roundRect(-12, -64, 24, 64, 10).fill(sil);
      st.g.roundRect(-12, -64, 24, 64, 10).stroke({ width: 1.2, color: sky.rimLight, alpha: 0.6 });
      st.soul.alpha = 0.6 + 0.2 * Math.sin(this.t * 1.6 + st.x);
    }
  }

  private showTip(stone: Stone, e: FederatedPointerEvent) {
    this.tooltip.innerHTML = `<div class="st-poem">「${stone.text}」</div><div class="st-author">—— 神明 ${stone.author}</div>`;
    this.tooltip.classList.add('show');
    this.moveTip(e);
  }
  private moveTip(e: FederatedPointerEvent) {
    const rect = this.stage.app.canvas.getBoundingClientRect();
    this.tooltip.style.left = `${rect.left + e.global.x + 14}px`;
    this.tooltip.style.top = `${rect.top + e.global.y - 10}px`;
  }
}

void rand;
